import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useContent } from "@/lib/content";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const target = parseInt(value, 10) || 0;
    if (target === 0) { setN(0); return; }
    let cur = 0;
    const step = Math.max(1, Math.ceil(target / 60));
    const id = setInterval(() => {
      cur += step;
      if (cur >= target) { cur = target; clearInterval(id); }
      setN(cur);
    }, 24);
    return () => clearInterval(id);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export default function Stats() {
  const { lang } = useI18n();
  const { content } = useContent();
  const items = content?.stats || [];
  if (!items.length) return null;
  return (
    <section className="relative py-12 sm:py-16 lg:py-24 border-y border-border/40 bg-muted/30" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {items.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="text-center lg:text-left"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gradient">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-sm text-muted-foreground font-medium">
                {lang === "ru" ? s.label_ru : s.label_en}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
